import { useSession } from "@core/hooks/useSession";
import { api } from "@core/services/nocd-api";
import { useQuery, UseQueryResult } from "react-query";

import transformPost from "../transformers/transformPost";
import { Post, PostExt, PostId } from "../types";

export const getPostQueryKey = (
  postId: PostId,
  accessToken?: string
): string[] => ["post-thread", String(postId), accessToken].filter(Boolean);

export interface GetPostResponse {
  nested_replies: Post[];
  replies: Post[];
  post: Post;
}

export interface PostResult {
  nested_replies: Post[];
  replies: PostExt[];
  post: PostExt;
}

export const getPost = (
  postId: PostId,
  accessToken?: string,
  signal?: AbortSignal
): Promise<PostResult> =>
  api
    .get<GetPostResponse>(
      `/v1/posts/${postId}?minimize_response_size=1`,
      accessToken
        ? {
            headers: { Authorization: accessToken },
            signal,
          }
        : { signal }
    )
    .then(({ data }) => ({
      ...data,
      post: transformPost(data.post),
      replies:
        Array.isArray(data.replies) &&
        data.replies.map((r) => transformPost(r)),
    }))
    .then((post) => {
      const result = {
        ...post,
        replies: post?.replies.filter(({ is_visible }) => is_visible),
      };

      if (!accessToken) {
        const replies = result.replies
          ?.reduce((acc, reply) => {
            if (reply.db_depth > 1) {
              const parent = acc.find(({ id }) => id === reply.post_replied_to);

              if (parent) {
                parent.replies.push(reply);
              }
            } else {
              acc.push({ ...reply, replies: [] });
            }

            return acc;
          }, [] as PostExt[])
          ?.sort((a, b) => {
            if (a.likes !== b.likes) {
              return b.likes - a.likes;
            }
            return a.id - b.id;
          });

        const repliesToShow: PostExt[] = [];
        replies?.forEach((reply) => {
          repliesToShow.push(reply);
          repliesToShow.push(...reply.replies);
        });
        result.replies = repliesToShow;
      }

      return result;
    });

export const usePost = (postId: PostId): UseQueryResult<PostResult, Error> => {
  const { data: session } = useSession();
  const { accessToken } = session ?? {};

  return useQuery({
    queryKey: getPostQueryKey(postId, accessToken),
    queryFn: ({ signal }) => getPost(postId, accessToken, signal),
  });
};

export const getRelatedPostsQueryKey = (postId: PostId): string[] => [
  "related-posts",
  String(postId),
];

interface GetRelatedPostsResponse {
  related_posts: PostExt[];
}

export const getRelatedPosts = (
  postId: PostId,
  accessToken?: string,
  signal?: AbortSignal
): Promise<PostExt[]> =>
  api
    .get<GetRelatedPostsResponse>(
      `/v1/community/posts/${postId}/related`,
      accessToken
        ? {
            headers: { Authorization: accessToken },
            signal,
          }
        : { signal }
    )
    .then(
      ({ data }) =>
        data.related_posts.filter((post) => post.post_type === "text") || []
    );

export const useRelatedPosts = (
  postId: PostId
): UseQueryResult<PostExt[], Error> => {
  const { data: session } = useSession();
  const { accessToken } = session ?? {};

  return useQuery({
    queryKey: getRelatedPostsQueryKey(postId),
    queryFn: ({ signal }) =>
      getRelatedPosts(postId, accessToken || null, signal),
  });
};
